import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { DeadlineBadge } from '@/entities/board';
import { taskPath, useMyTasksSummary } from '@/entities/my-tasks';
import { ErrorState, Icons } from '@/shared/ui';

import styles from './HomeDashboard.module.css';

/**
 * A small summary of the viewer's own work - NOT a second dashboard: overdue / due-today counts,
 * the three most urgent tasks and a link to the full planner. The selection comes from the same
 * server rule as the planner page, so the two can never disagree.
 */
export function MyWorkPanel() {
  const { t } = useTranslation();
  const summary = useMyTasksSummary();
  const data = summary.data;
  const boardName = new Map((data?.boards ?? []).map((board) => [board.id, board.name]));
  return (
    <aside className={styles.myWork} aria-labelledby="my-work-title" data-testid="my-work">
      <header className={styles.myWorkHead}>
        <h2 id="my-work-title" className={styles.sectionTitle}>
          {t('home.myWork.title')}
        </h2>
        <Link to="/app/my-tasks" className={styles.viewAll} data-testid="my-tasks-link">
          {t('home.myWork.openAll')} <Icons.arrowRight />
        </Link>
      </header>
      {summary.isError && (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      )}
      {summary.isPending && <p className={styles.empty}>{t('common.loading')}</p>}
      {data && (
        <>
          <dl className={styles.counters}>
            <div className={`${styles.counter} ${data.overdue > 0 ? styles.counterAlert : ''}`}>
              <dt>{t('home.myWork.overdue')}</dt>
              <dd data-testid="my-work-overdue">{data.overdue}</dd>
            </div>
            <div className={styles.counter}>
              <dt>{t('home.myWork.today')}</dt>
              <dd data-testid="my-work-today">{data.today}</dd>
            </div>
          </dl>
          {data.preview.length === 0 ? (
            <p className={styles.empty}>{t('home.myWork.empty')}</p>
          ) : (
            <ul className={styles.previewList}>
              {data.preview.map((card) => (
                <li key={card.id}>
                  <Link
                    to={taskPath(card.placement.board_id, card.id)}
                    className={styles.previewItem}
                    data-testid="my-work-task"
                  >
                    <span className={styles.previewTitle}>{card.title}</span>
                    <span className={styles.previewMeta}>
                      <span className={styles.previewBoard}>
                        {boardName.get(card.placement.board_id)}
                      </span>
                      <span aria-hidden>·</span>
                      {card.deadline ? (
                        <DeadlineBadge card={card} className={styles.previewTime} />
                      ) : (
                        <span>{t('myTasks.groups.none')}</span>
                      )}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </aside>
  );
}
